import { RadiusUnit } from '../../types/symbology.types';

export const zoomToScaleDenominator = (zoom: number): number => {
  const scale = 591657550.5 / Math.pow(2, zoom);
  return scale;
};

const METERS_PER_UNIT: Record<Exclude<RadiusUnit, 'pixels'>, number> = {
  miles: 1609.344,
  kilometers: 1000,
  meters: 1,
};

// vec:Heatmap computes on a grid this many output pixels wide, so any radius
// under one cell renders as the same minimum blob no matter its value — this is
// what makes small radii indistinguishable, not the pixel rounding.
export const HEATMAP_PIXELS_PER_CELL = 4;

// One whole cell, so a radius is never sub-cell and silently flat. Ceiling
// mirrors the render buffer's.
const MIN_RADIUS_PIXELS = HEATMAP_PIXELS_PER_CELL;
const MAX_RADIUS_PIXELS = 512;

const clampPixels = (px: number): number =>
  Number.isFinite(px) ? Math.min(Math.max(Math.round(px), MIN_RADIUS_PIXELS), MAX_RADIUS_PIXELS) : MIN_RADIUS_PIXELS;

/**
 * vec:Heatmap only understands radiusPixels, so a metric radius has to be
 * converted against the image it renders into. The heatmap is drawn as ONE
 * fixed-size image over the data extent, so meters-per-pixel is constant per
 * layer — no zoom dependency, and no need to restyle on camera move.
 */
export const radiusToPixels = (
  radius: number,
  unit: RadiusUnit,
  bbox: { minLon: number; minLat: number; maxLon: number; maxLat: number },
  imageWidth: number,
): number => {
  const metersPerUnit = METERS_PER_UNIT[unit as Exclude<RadiusUnit, 'pixels'>];
  // Legacy layers carry no unit and have always been drawn as pixels.
  if (!metersPerUnit) return clampPixels(radius);

  // EPSG:4326 image, so scale it by longitude at the bbox's mid-latitude.
  const centerLat = (((bbox.minLat + bbox.maxLat) / 2) * Math.PI) / 180;
  const metersPerPixel = ((bbox.maxLon - bbox.minLon) * 111320 * Math.cos(centerLat)) / imageWidth;
  if (!(metersPerPixel > 0)) return clampPixels(radius);

  return clampPixels((radius * metersPerUnit) / metersPerPixel);
};

export const generateColorMapEntries = (colors: string[]): string => {
  return colors
    .map((color, i) => {
      const quantity = Number((i / (colors.length - 1)).toFixed(2));
      const isFirst = i === 0;
      return `
        <ColorMapEntry 
            color="${color}"
            quantity="${quantity}"
            label="${isFirst ? 'nodata' : 'values'}"
            opacity="${isFirst ? 0 : 1}"
        />`;
    })
    .join('\n              ');
};
